import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as RebootSuccess } from '../../../../../images/icons/reboot_success.svg';
import { ReactComponent as RebootFailed } from '../../../../../images/icons/reboot_failed.svg';
import NewModal from '../../../../Shared/NewModal';
import { dimGrey } from '../../../../../mainTheme';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import {
  clearDeviceActionStatus,
  deviceReboot,
} from '../../../../../store/devices/actionCreators';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface RebootDialogProps {
  onClose: () => void;
  deviceId: number;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

export const RebootDialog: React.FC<RebootDialogProps> = ({
  onClose,
  deviceId,
}) => {
  const { actionStatus } = useSelector<
    RootState,
    { actionStatus: DevicesStore['actionStatus'] }
  >((state) => ({
    actionStatus: state.devices.actionStatus,
  }));

  const dispatch = useDispatch();
  const [step, setStep] = useState<'confirm' | 'success' | 'failed'>('confirm');

  const handleReboot = () => {
    dispatch(deviceReboot(deviceId));
  };

  useEffect(() => {
    if (actionStatus.type === 'reboot') {
      if (actionStatus.success) {
        setStep('success');
      } else if (actionStatus.success === false) {
        setStep('failed');
      }
    }
  }, [actionStatus]);

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('confirm');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderConfirmView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Reboot Hub</Typography>
      <Typography sx={typography.subText}>
        The Hub will be offline temporarily during the reboot process. All data
        will be saved.
      </Typography>
      <Typography sx={typography.subText}>
        If you wish to continue, click <strong>Reboot</strong>.
      </Typography>
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return renderConfirmView();
      case 'success':
        return (
          <StatusView
            icon={<RebootSuccess />}
            title="Reboot successful"
            message="The Hub will show the home screen and be ready for the next medication event."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<RebootFailed />}
            title="Reboot failed"
            message="Please try again at a later time."
          />
        );
    }
  };

  const actionConfig = {
    success: { text: 'OK', onClick: handleClose },
    failed: { text: 'Try again', onClick: handleReset },
    confirm: { text: 'Reboot', onClick: handleReboot },
  };

  return (
    <NewModal
      showCloseX
      title="Reboot"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: {
          ...actionConfig[step],
        },
        secondaryAction:
          step === 'confirm'
            ? {
                text: 'Back',
                onClick: handleClose,
              }
            : undefined,
      }}
    >
      {renderContent()}
    </NewModal>
  );
};
